/**
 * AnisotropyModifier — hard-surface anisotropic specular (brushed metal,
 * machined barrels, architectural panels) on three's physical node material
 * (shader additions wave, 2026-09-15; KHR_materials_anisotropy; Filament §4.7;
 * Burley 2012).
 *
 * three r185's PhysicalLightingModel already evaluates an ANISOTROPIC GGX
 * (`BRDF_GGX_Multiscatter({ USE_ANISOTROPY })`, with the matching anisotropic
 * visibility term) once a MeshPhysicalNodeMaterial carries `anisotropyNode`:
 * a vec2 whose LENGTH is the strength and whose DIRECTION rotates the tangent
 * frame —
 *   αT = mix(α, 1, strength²),  αB = α              (the glTF parameterisation)
 *   T' = T·dir.x + B·dir.y,     B' = B·dir.x − T·dir.y
 * The direction is that of INCREASED roughness — the highlight's long axis,
 * perpendicular to the micro-grooves. This modifier supplies the vec2: a
 * constant (strength, rotation) or a glTF direction map (RG = a 0.5-centred
 * tangent-plane direction, B = strength), and keeps the CPU mirrors the
 * harness pins — the alpha split and the anisotropic NDF.
 *
 * Requires a MeshPhysicalNodeMaterial (or a classic MeshPhysicalMaterial,
 * which three converts at first render); converting a standard material is
 * the caller's job (Atlas: `toPhysicalMaterial`). Without a tangent attribute
 * three derives the frame from screen derivatives, as it does for normal maps.
 */
import type * as THREE from 'three/webgpu';
import { Fn, uniform, vec2, texture } from 'three/tsl';

export interface AnisotropyConfig {
  /** 0..1 (default 0.5). */
  strength?: number;
  /** Direction of the highlight's long axis in the tangent plane, radians (default 0 = along the tangent). */
  rotation?: number;
  /** Optional glTF anisotropy texture: RG direction (0.5-centred), B strength. */
  directionMap?: THREE.Texture | null;
}

export interface AnisotropyHandle {
  strength: { value: number };
  rotation: { value: number };
  usesMap: boolean;
}

/** The vec2 three consumes: direction × strength. */
export function anisotropyVectorCpu(strength: number, rotation: number): [number, number] {
  const s = Math.min(1, Math.max(0, strength));
  return [Math.cos(rotation) * s, Math.sin(rotation) * s];
}

/** glTF alpha split from perceptual roughness and strength. */
export function anisotropyAlphasCpu(roughness: number, strength: number): { alphaT: number; alphaB: number } {
  const a = Math.max(1e-4, roughness * roughness);
  const s = Math.min(1, Math.max(0, strength));
  return { alphaT: a + (1 - a) * s * s, alphaB: a };
}

/** Anisotropic GGX NDF: D = 1 / (π αT αB [ (h·T/αT)² + (h·B/αB)² + (h·N)² ]²). */
export function anisotropicGgxDCpu(hT: number, hB: number, hN: number, alphaT: number, alphaB: number): number {
  const at = Math.max(1e-4, alphaT), ab = Math.max(1e-4, alphaB);
  const q = (hT * hT) / (at * at) + (hB * hB) / (ab * ab) + hN * hN;
  return 1 / (Math.PI * at * ab * q * q);
}

export function applyAnisotropy(
  material: THREE.Material & {
    anisotropyNode?: unknown; anisotropy?: number; needsUpdate?: boolean;
    isMeshPhysicalMaterial?: boolean; isMeshPhysicalNodeMaterial?: boolean;
  },
  config?: AnisotropyConfig,
): AnisotropyHandle | null {
  if (!(material.isMeshPhysicalMaterial || material.isMeshPhysicalNodeMaterial)) return null;
  const strength = config?.strength ?? 0.5;
  const uStrength = uniform(strength);
  const uRotation = uniform(config?.rotation ?? 0);
  const map = config?.directionMap ?? null;

  material.anisotropyNode = Fn(() => {
    const dir = vec2(uRotation.cos(), uRotation.sin());
    if (map) {
      const t = texture(map);
      const md = t.xy.mul(2).sub(1); // the map's tangent-plane direction
      // rotate the map's direction by the authored rotation; scale by both strengths
      const rx = md.x.mul(dir.x).sub(md.y.mul(dir.y));
      const ry = md.x.mul(dir.y).add(md.y.mul(dir.x));
      return vec2(rx, ry).mul(t.z).mul(uStrength);
    }
    return dir.mul(uStrength);
  })();
  // The scalar keeps `useAnisotropy` and the classic path honest.
  material.anisotropy = strength;
  material.needsUpdate = true;

  return {
    strength: uStrength as unknown as { value: number },
    rotation: uRotation as unknown as { value: number },
    usesMap: !!map,
  };
}
